/**
 * Peut-on proposer Bancontact, et pour quel montant ?
 *
 * La règle vit ici, **sans réseau et sans client Supabase**, pour la même
 * raison que `orders/devis.ts` : `gateway.supabase.ts` importe le client, qui
 * lève au chargement si les variables d'environnement manquent. Rien de ce
 * qu'il contient ne s'éprouve. Une règle qu'on veut tester vit à côté, pas
 * dedans.
 *
 * ## Ce que la règle décide
 *
 * Trois situations, et elles n'appellent pas le même écran :
 *
 * - **Rien à payer.** Un bon cadeau a tout couvert, ou la provision de la
 *   famille absorbe la facture. Aucune session Stripe n'est créée — Stripe
 *   refuse un montant nul — et surtout, proposer de payer zéro euro ferait
 *   croire qu'il reste quelque chose.
 * - **Trop peu pour Stripe.** En dessous du minimum d'encaissement, l'appel
 *   échouerait côté Stripe, après le clic, sans que le parent comprenne
 *   pourquoi. On ne propose pas un bouton qui ne peut pas marcher.
 * - **Payable.** Le montant est celui que la BASE a décidé, jamais celui que
 *   l'écran a calculé.
 *
 * ## Le montant ne voyage pas
 *
 * `montantCentimes` sert **à afficher**, jamais à encaisser. La fonction edge
 * relit elle-même `v_invoice_balances.outstanding_cents` avant de créer la
 * session : le corps de la requête ne porte qu'un `invoice_id`, donc il n'y a
 * rien à falsifier. C'est la discipline de `create_order`, pour la même raison
 * — l'ancien `create-checkout` écrivait `unit_amount: Math.round(item.price)`,
 * ce que le navigateur lui donnait.
 */

/**
 * Le montant minimum qu'une transaction Stripe peut porter, en centimes.
 *
 * **À confirmer** auprès du compte : 0,50 EUR est la valeur annoncée pour
 * l'euro, mais elle n'a pas pu être vérifiée depuis cet environnement (le
 * proxy réseau bloque `stripe.com`).
 *
 * Mesuré en production le 5 septembre 2026 : sur 3 374 factures payantes,
 * **aucune** ne tombe entre 0 et 0,50 EUR. Ce garde-fou ne protège donc rien
 * aujourd'hui — il protège le jour où un bon cadeau laissera un solde de
 * quelques centimes.
 */
export const MINIMUM_STRIPE_CENTIMES = 50;

/** Ce que la fenêtre de paiement doit montrer. */
export type OffreDePaiement =
  /** Payable en ligne : le bouton Bancontact est proposé. */
  | { forme: 'payable'; montantCentimes: number }
  /** Rien n'est dû : il n'y a pas de paiement à proposer, seulement à confirmer. */
  | { forme: 'rien_a_payer' }
  /** Un solde existe, mais Stripe ne peut pas l'encaisser. Virement seulement. */
  | { forme: 'trop_faible'; montantCentimes: number };

/** Ce que `create_order` rend, réduit à ce dont cette règle a besoin. */
export interface ResteACharge {
  remaining_cents: number;
}

/**
 * Décide ce que la fenêtre propose, à partir du reste rendu par la base.
 *
 * Un `remaining_cents` absent ou illisible est traité comme « rien à payer » :
 * mieux vaut ne pas proposer un paiement qu'en proposer un pour un montant
 * qu'on ne connaît pas.
 */
export function offreDePaiement(commande: ResteACharge | null | undefined): OffreDePaiement {
  const reste = Number(commande?.remaining_cents);

  if (!Number.isFinite(reste) || reste <= 0) return { forme: 'rien_a_payer' };
  if (reste < MINIMUM_STRIPE_CENTIMES) return { forme: 'trop_faible', montantCentimes: reste };

  return { forme: 'payable', montantCentimes: reste };
}

/**
 * L'issue du passage chez Stripe, telle que l'URL de retour la porte.
 *
 * Stripe rappelle deux adresses : `success_url` quand le parent est allé au
 * bout, `cancel_url` quand il a renoncé ou que sa banque a refusé. Aucune des
 * deux ne fait autorité sur le paiement — **c'est le webhook qui écrit**. Ces
 * valeurs ne servent qu'à choisir le message affiché.
 */
export type IssueDePaiement = 'ok' | 'echec' | null;

/** Lit `?paiement=` sans faire confiance à ce qu'on y trouve. */
export function lireIssueDePaiement(valeur: string | null | undefined): IssueDePaiement {
  if (valeur === 'ok') return 'ok';
  if (valeur === 'echec') return 'echec';
  return null;
}

/**
 * Les deux adresses de retour, construites à partir de l'URL de confirmation.
 *
 * Les deux mènent à la **même page** — celle qui dit au parent que son enfant
 * est inscrit. Une page d'échec séparée dupliquerait ce message, et une
 * duplication finit toujours par diverger : c'est la leçon de l'étape d'envoi
 * qui vivait dans un seul écran et que le suivant oubliait.
 *
 * `{CHECKOUT_SESSION_ID}` est laissé tel quel : Stripe le remplace lui-même.
 */
export function adressesDeRetour(origine: string, urlConfirmation: string): {
  success_url: string;
  cancel_url: string;
} {
  const base = `${origine.replace(/\/+$/, '')}${urlConfirmation}`;
  const separateur = urlConfirmation.includes('?') ? '&' : '?';
  return {
    success_url: `${base}${separateur}paiement=ok&session={CHECKOUT_SESSION_ID}`,
    cancel_url: `${base}${separateur}paiement=echec`,
  };
}

/**
 * Le solde d'une facture, tel que `v_invoice_balances` le rend.
 *
 * `null` tant qu'il n'a pas été lu : c'est un état distinct de « rien à
 * payer », et les confondre est précisément ce qui a produit le défaut décrit
 * plus bas.
 */
export interface SoldeDeFacture {
  outstanding_cents: number;
  settlement: string;
}

/** Ce que l'écran de confirmation doit montrer du paiement. */
export interface EtatAffiche {
  /** Le bandeau du haut. */
  bandeau: 'paye' | 'echec' | 'en_attente' | null;
  /** Le reste réellement dû, ou `null` tant qu'on ne le sait pas. */
  resteCentimes: number | null;
  /** Le bloc de virement et son QR. */
  montrerVirement: boolean;
  /** Le document peut-il être produit sans mentir sur son état ? */
  peutProduireLaFacture: boolean;
}

/**
 * Décide ce que la page affiche, à partir de l'issue Stripe ET du solde relu.
 *
 * ## Le défaut que cette règle existe pour empêcher
 *
 * Relevé le 5 septembre 2026, sur la facture CDV-TEST-2602033 : le bandeau
 * annonçait « Paiement reçu — merci » pendant que le bloc juste en dessous
 * affichait « Reste à payer : 3,00 €, dans les 20 jours », QR de virement
 * compris.
 *
 * La cause n'était pas le paiement : c'était que l'écran lisait le reste dans
 * le paramètre d'URL `?remaining=`, **calculé au moment de la commande**. Ce
 * chiffre ne bouge jamais. Il était donc faux dès qu'un paiement passait —
 * et il l'aurait été même avec un webhook parfait.
 *
 * Un paramètre d'URL n'est pas une source de vérité : il est figé, et
 * n'importe qui peut le réécrire à la main. Le solde se relit en base.
 *
 * ## L'attente est un état, pas un défaut
 *
 * Stripe attend jusqu'à dix secondes la réponse du webhook avant de rediriger,
 * donc le solde est presque toujours à jour à l'arrivée. Presque. Quand il ne
 * l'est pas, on affiche « en_attente » — jamais « il reste 3,00 € à payer » à
 * quelqu'un qui vient de payer.
 */
export function etatAffiche(
  issue: IssueDePaiement,
  solde: SoldeDeFacture | null,
): EtatAffiche {
  const connu = solde !== null && Number.isFinite(Number(solde.outstanding_cents));
  const reste = connu ? Number(solde!.outstanding_cents) : null;
  const solde_ = reste !== null && reste <= 0;

  if (issue === 'ok') {
    /*
      Le parent revient de Stripe. Tant que la base ne confirme pas, on dit
      l'attente : ni « payé » (on ne le sait pas encore), ni le reste dû (ce
      serait démentir ce qu'il vient de faire).
    */
    if (!connu) return {
      bandeau: 'en_attente', resteCentimes: null,
      montrerVirement: false, peutProduireLaFacture: false,
    };
    if (solde_) return {
      bandeau: 'paye', resteCentimes: 0,
      montrerVirement: false, peutProduireLaFacture: true,
    };
    return {
      bandeau: 'en_attente', resteCentimes: reste,
      montrerVirement: false, peutProduireLaFacture: false,
    };
  }

  /*
    Échec, ou arrivée normale sans passer par Stripe. Le virement s'affiche dès
    qu'il reste quelque chose — et seulement alors : un QR pour zéro euro ferait
    croire qu'il reste à payer.
  */
  return {
    bandeau: issue === 'echec' ? 'echec' : null,
    resteCentimes: reste,
    montrerVirement: connu && !solde_,
    peutProduireLaFacture: connu,
  };
}

/** Le message central de la page de confirmation, en trois morceaux. */
export interface MessageCentral {
  titre: string;
  /** Le sous-titre, coupé pour que le milieu s'imprime en gras. */
  avant: string;
  fort: string;
  apres: string;
}

/**
 * Ce que la page dit en gros, selon ce qu'on sait du paiement.
 *
 * ## Le défaut que cette règle existe pour empêcher
 *
 * Signalé le 6 septembre 2026, après le premier paiement réel : la page
 * affichait, à un parent qui venait de régler par Bancontact,
 *
 *   « Sa place est réservée dès maintenant, **même si votre paiement ne nous
 *     est pas encore parvenu**. »
 *
 * Cette phrase est juste — mais pour quelqu'un qui n'a PAS payé. Servie à
 * quelqu'un qui vient de payer, elle sème le doute : il se demande si son
 * argent est arrivé, et cherche ce qu'il aurait oublié de faire.
 *
 * Le correctif précédent avait supprimé la contradiction sur le MONTANT
 * (le « reste à payer » et le QR). Il n'avait pas touché à cette phrase-là,
 * parce qu'elle est statique et qu'aucun chiffre ne la démentait.
 *
 * ## Une page, trois messages — et non trois pages
 *
 * La tentation est de faire un écran séparé pour le paiement réussi. On ne le
 * fait pas : le message « votre enfant est inscrit » vivrait alors à deux
 * endroits, et deux endroits finissent par diverger. C'est la leçon de l'étape
 * d'envoi qui vivait dans un seul écran et que le suivant oubliait.
 *
 * C'est donc la MÊME page, dont la phrase centrale suit ce que la base dit.
 */
export function messageCentral(bandeau: EtatAffiche['bandeau']): MessageCentral {
  if (bandeau === 'paye') {
    return {
      titre: "C'est réglé : votre enfant est inscrit",
      avant: 'Sa place est réservée et votre facture est acquittée. ',
      fort: "Vous n'avez plus rien à faire.",
      apres: '',
    };
  }

  if (bandeau === 'en_attente') {
    return {
      titre: "C'est confirmé : votre enfant est inscrit",
      avant: 'Sa place est réservée dès maintenant. ',
      fort: 'Votre paiement est parti',
      apres: " : nous attendons la confirmation de votre banque, d'ordinaire quelques secondes.",
    };
  }

  /*
    Aucun paiement en ligne, ou un paiement qui n'a pas abouti. C'est ici, et
    ici seulement, que la mention « même si votre paiement ne nous est pas
    encore parvenu » a un sens — et elle est alors essentielle : c'est la
    promesse que l'inscription ne dépend pas de l'argent.
  */
  return {
    titre: "C'est confirmé : votre enfant est inscrit",
    avant: 'Sa place est réservée dès maintenant, ',
    fort: "même si votre paiement ne nous est pas encore parvenu",
    apres: ". Vous n'avez rien d'autre à faire pour valider l'inscription.",
  };
}
